import { StyleSheet, Text, View } from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";
import {
  Button,
  Icon,
  NoticeBox,
  ThemedTable,
  type ThemedTableColumn,
  type ThemedTableHeaderColumn,
  ThemedText,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { SecondaryButton } from "@/components/SecondaryButton";
import { ChefHatModal } from "@/components/modals/ChefHatModal";

type CancelConfirmationModalProps = {
  visible: boolean;
  /**
   * Translation namespace holding this dialog's `header`, `title`,
   * `tableHeaderText`, `notice`, `cancelButtonText` and `submitButtonText`
   * (e.g. `"guestOrders.cancelModal"`).
   */
  translationPrefix: string;
  /** Summary of what is about to be cancelled, rendered as a single row. */
  headerColumns: ThemedTableHeaderColumn[];
  rowColumns: ThemedTableColumn[];
  isSubmitting?: boolean;
  validationError?: string | null;
  onDismiss: () => void;
  onConfirm: () => void;
};

/**
 * Shared "are you sure you want to cancel this?" dialog. Callers supply the
 * summary table and the translation namespace; everything else is identical
 * across the cancellation flows.
 */
export function CancelConfirmationModal({
  visible,
  translationPrefix,
  headerColumns,
  rowColumns,
  isSubmitting = false,
  validationError = null,
  onDismiss,
  onConfirm,
}: CancelConfirmationModalProps) {
  const { colors, fonts } = useCustomTheme();
  const { t } = useTranslation();

  return (
    <ChefHatModal
      visible={visible}
      icon={{ icon: "close-circle", iconSet: "material-community" }}
      iconSize={44}
      onDismiss={onDismiss}
    >
      <View style={styles.header}>
        <Icon
          iconSet="font-awesome"
          icon="bell-exclamation"
          size={24}
          color={colors.danger}
        />
        <Text style={[styles.headerText, fonts.bold, { color: colors.danger }]}>
          {t(`${translationPrefix}.header`)}
        </Text>
      </View>

      <Text style={[styles.title, fonts.bold, { color: colors.text }]}>
        {t(`${translationPrefix}.title`)}
      </Text>

      <Text style={[styles.tableHeaderText, fonts.regular]}>
        {t(`${translationPrefix}.tableHeaderText`)}
      </Text>

      <ThemedTable headerColumns={headerColumns}>
        <ThemedTable.Row columns={rowColumns} />
      </ThemedTable>

      <View style={styles.noticeBox}>
        <NoticeBox textContent={t(`${translationPrefix}.notice`)} />
      </View>

      <View style={styles.validationErrorContainer}>
        <ThemedText type="danger" style={styles.validationErrorText}>
          {validationError ?? ""}
        </ThemedText>
      </View>

      <View style={styles.actions}>
        <SecondaryButton
          label={t(`${translationPrefix}.cancelButtonText`)}
          onPress={onDismiss}
          iconName="close"
          iconPosition="left"
          disabled={isSubmitting}
        />

        <Button
          buttonText={t(`${translationPrefix}.submitButtonText`)}
          onPress={onConfirm}
          disabled={isSubmitting}
          buttonStyle={[
            styles.primaryButton,
            { backgroundColor: colors.border },
          ]}
          buttonTextStyle={[
            styles.primaryButtonText,
            fonts.bold,
            { color: colors.background },
          ]}
          iconName="arrow-right"
          iconColor={colors.background}
          iconPosition="right"
          iconSize={24}
        />
      </View>
    </ChefHatModal>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 24,
  },
  headerText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  title: {
    fontSize: 32,
    lineHeight: 40,
    marginBottom: 48,
  },
  tableHeaderText: {
    fontSize: 20,
    lineHeight: 25,
    marginBottom: 24,
  },
  noticeBox: {
    marginTop: 24,
    marginBottom: 32,
  },
  validationErrorContainer: {
    marginBottom: 8,
    minHeight: 36,
  },
  validationErrorText: {
    fontSize: 16,
    textAlign: "center",
  },
  actions: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
  },
  primaryButton: {
    borderRadius: 56,
    height: 56,
    paddingHorizontal: 24,
  },
  primaryButtonText: {
    fontSize: 16,
  },
});
